<template>
  <!-- Follow up > Emails Going Out (item (b), batch 3; on the Follow up page since 04/10/2026).
       The list design is the standard list's (MyActivityTableForLazyLoadingWithResize, used by
       Events and All Activities): the toolbar with refresh and Global Search, a striped grid with
       resizable, sortable columns, the frozen Actions column and the 25/50/100 pager. The filters
       sit in a row under the column headers. -->
  <div class="followup-list">
    <p id="goingOutHeadline" class="followup-explain">{{ headline }}</p>

    <Toolbar class="p-mb-4">
      <template slot="left">
        <div class="p-inputgroup">
          <Button id="goingOutRefresh" icon="pi pi-fw pi-refresh" class="p-mr-1" @click="load" />
          <InputText id="goingOutSearch" v-model="searchText" placeholder="Global Search" />
          <Button icon="pi pi-times p-pr-1" class="p-mr-1" @click="searchText = ''" />
        </div>
      </template>
      <template slot="right">
        <Button v-if="filtered" id="goingOutClearFilters" label="Clear filters" icon="pi pi-filter-slash"
          class="p-button-text" @click="clearFilters" />
      </template>
    </Toolbar>

    <OverlayPanel ref="opActions" appendTo="body" :showCloseIcon="true" style="width: 250px;">
      <div v-if="actionRow" class="followup-actions">
        <Button id="goingOutOpenQuote" label="Open the quote's follow-up" icon="pi pi-external-link"
          class="p-button-text p-button-plain" @click="openQuote(actionRow)" />
        <Button v-if="actionRow.canAct" id="goingOutChangeEmail" label="Edit, pause or stop this email" icon="pi pi-pencil"
          class="p-button-text p-button-plain" @click="changeEmail(actionRow)" />
      </div>
    </OverlayPanel>

    <DataTable
      :value="rows"
      :paginator="true"
      class="p-datatable-striped p-datatable-sm p-datatable-gridlines p-datatable-customers"
      dataKey="followupId"
      :rowHover="true"
      :loading="loading"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :rowsPerPageOptions="[25, 50, 100]"
      currentPageReportTemplate="{first} to {last} of {totalRecords} Emails"
      :rows="25"
      :resizableColumns="true"
      columnResizeMode="expand"
      :scrollable="true"
      scrollHeight="400px"
      frozenWidth="50px"
    >
      <template #empty>No automatic emails are due in this period.</template>
      <template #loading>Loading the emails going out. Please wait.</template>

      <Column header="Actions" headerStyle="width: 50px; height: 41px; padding: 10px 0px;" :frozen="true">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell">
            <Button :id="'goingOutActions' + slot.data.followupId" icon="pi pi-ellipsis-h"
              class="p-button-rounded p-button-text" @click="toggleActions($event, slot.data)" />
          </div>
        </template>
      </Column>
      <Column header="Send" headerStyle="width: 70px; height: 41px;">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell" title="Untick to skip this email; the quote's later emails still go">
            <Checkbox :id="'send' + slot.data.followupId" :binary="true"
              :modelValue="!slot.data.skipped" :disabled="busyId === slot.data.followupId || !slot.data.canAct"
              @input="toggle(slot.data)" />
          </div>
        </template>
      </Column>
      <Column field="dueAt" sortField="dueSort" header="Due" :sortable="true" headerStyle="width: 150px; height: 41px;">
        <template #filter><div class="filter-cell">
          <div class="date-range">
            <Calendar id="goingOutFrom" v-model="filters.from" dateFormat="dd/mm/yy" placeholder="From" :showButtonBar="true" />
            <Calendar id="goingOutTo" v-model="filters.to" dateFormat="dd/mm/yy" placeholder="To" :showButtonBar="true" />
          </div>
        </div></template>
        <template #body="slot"><div class="cell">{{ slot.data.dueAt }}</div></template>
      </Column>
      <Column field="quoteRef" header="Quote" :sortable="true" headerStyle="width: 120px; height: 41px;">
        <template #filter><div class="filter-cell">
          <InputText id="goingOutQuote" v-model="filters.quote" class="p-column-filter" placeholder="Quote number" />
        </div></template>
        <template #body="slot">
          <div class="cell">
            <router-link :to="'/enquiries/' + slot.data.oppId + '/followup'">{{ slot.data.quoteRef }}</router-link>
          </div>
        </template>
      </Column>
      <Column field="customerName" header="Customer" :sortable="true" headerStyle="width: 200px; height: 41px;">
        <template #filter><div class="filter-cell">
          <InputText id="goingOutCustomer" v-model="filters.customer" class="p-column-filter" placeholder="Customer" />
        </div></template>
        <template #body="slot"><div class="cell"><span class="clamp" :title="slot.data.customerName">{{ slot.data.customerName }}</span></div></template>
      </Column>
      <Column field="contactName" header="Contact" :sortable="true" headerStyle="width: 220px; height: 41px;">
        <template #filter><div class="filter-cell">
          <InputText id="goingOutContact" v-model="filters.contact" class="p-column-filter" placeholder="Contact" />
        </div></template>
        <template #body="slot">
          <div class="cell">
            <div class="no-break" :title="slot.data.contactName">{{ slot.data.contactName }}</div>
            <div class="no-break" :title="slot.data.contactEmail"><small>{{ slot.data.contactEmail }}</small></div>
          </div>
        </template>
      </Column>
      <Column field="subject" header="Email" :sortable="true" headerStyle="width: 280px; height: 41px;">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell"><span class="clamp" :title="'Email ' + slot.data.stepNo + ': ' + slot.data.subject">Email {{ slot.data.stepNo }}: {{ slot.data.subject }}</span></div>
        </template>
      </Column>
      <Column v-if="data && data.allUsers" field="salespersonName" header="Salesperson" :sortable="true" headerStyle="width: 170px; height: 41px;">
        <template #filter><div class="filter-cell">
          <Dropdown id="goingOutSalesperson" v-model="salespersonId" :options="peopleOptions" optionLabel="name"
            optionValue="id" placeholder="Everyone" class="p-column-filter" />
        </div></template>
        <template #body="slot"><div class="cell">{{ slot.data.salespersonName }}</div></template>
      </Column>
      <Column header="Note" headerStyle="width: 240px; height: 41px;">
        <template #filter><div class="filter-cell"><span class="filter-spacer"></span></div></template>
        <template #body="slot">
          <div class="cell">
            <span v-if="slot.data.skipped" class="clamp">Skipped by {{ slot.data.skipRequestedBy || 'a user' }}</span>
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import FollowupService, { errorMessage } from '@/service/FollowupService'
import { salespeople, headline, dueSortKey, filterGoingOut } from './emailsGoingOut'

const noFilters = () => ({ from: null, to: null, quote: '', customer: '', contact: '' })

export default {
  // item (b), batch 3: each user sees their own; Admin (settings users) sees all and can filter by salesperson
  name: 'EmailsGoingOut',
  data: () => ({ followupService: new FollowupService(), data: null, loading: false, busyId: null, salespersonId: null,
    searchText: '', filters: noFilters(), actionRow: null }),
  computed: {
    headline() { return headline(this.data) },
    people() { return salespeople(this.data && this.data.rows) },
    peopleOptions() { return [{ id: null, name: 'Everyone' }].concat(this.people) },
    rows() {
      return filterGoingOut(this.data && this.data.rows, { ...this.filters, search: this.searchText, salespersonId: this.salespersonId })
    },
    filtered() {
      var f = this.filters
      return !!(f.from || f.to || f.quote || f.customer || f.contact || this.salespersonId !== null)
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      return this.followupService.goingOut()
        .then(d => {
          if (d && Array.isArray(d.rows)) d.rows.forEach(r => { r.dueSort = dueSortKey(r.dueAt) })
          this.data = d
        })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: errorMessage(err, 'The emails going out could not be loaded.'), life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    clearFilters() {
      this.filters = noFilters()
      this.salespersonId = null
    },
    toggleActions(event, row) {
      this.actionRow = row
      this.$refs.opActions.toggle(event)
    },
    openQuote(row) {
      if (this.$refs.opActions && this.$refs.opActions.hide) this.$refs.opActions.hide()
      this.$router.push('/enquiries/' + row.oppId + '/followup').catch(() => {})
    },
    /** Decision 3: the same page the reminder email links to. */
    changeEmail(row) {
      if (this.$refs.opActions && this.$refs.opActions.hide) this.$refs.opActions.hide()
      this.$router.push('/followupApproval/' + row.followupId + '?step=' + row.stepNo).catch(() => {})
    },
    /** Untick = skip this email (the quote's later ones still go); tick again = send it after all. */
    toggle(row) {
      this.busyId = row.followupId
      var call = row.skipped ? this.followupService.unskip(row.followupId) : this.followupService.skipNext(row.followupId, row.stepNo)
      return call
        .then(() => {
          row.skipped = !row.skipped
          if (row.skipped) row.skipRequestedBy = this.myName()
        })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not changed', detail: errorMessage(err, 'That email could not be changed.'), life: 5000 })
        })
        .finally(() => { this.busyId = null })
    },
    myName() {
      var p = this.$store && this.$store.getters && this.$store.getters['auth/getMyProfile']
      return p ? [p.firstName, p.lastName].filter(Boolean).join(' ') : 'you'
    }
  }
}
</script>

<style scoped>
.followup-explain { margin: 1rem 0 0.75rem; }
.cell { height: 52px; padding: 4px 0px; display: flex; flex-direction: column; justify-content: center; overflow: hidden; }
/* Every row the same height, so the frozen Actions column stays level with the rest; a long text shows two lines and the whole of it on hover. */
.clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
/* An address is never broken mid-word: one line, cut with an ellipsis, the whole of it on hover. */
.no-break { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.date-range { display: flex; flex-direction: column; gap: 4px; }
.date-range ::v-deep .p-calendar, .date-range ::v-deep input, .p-column-filter, ::v-deep .p-column-filter input { width: 100%; }
/* Filter cells one fixed height, so the frozen Actions header lines up with the rest. */
.filter-cell { height: 72px; display: flex; flex-direction: column; justify-content: center; }
.filter-spacer { display: block; height: 1px; }
/* The empty / loading message shows once, in the scrolling part, not cut off under the frozen Actions column too. */
::v-deep .p-datatable-frozen-view .p-datatable-emptymessage td { color: transparent; }
</style>
